<script setup lang="ts">
import { Loader2 } from 'lucide-vue-next'
import PreviewExpeditor from '@/views/domain/components/PreviewExpeditor.vue'

const props = defineProps<{
  domain: string | null
}>()

const { t } = useI18n()
const { getEmailSettings, saveEmailSettings, isLoading: isLoadingSettings } = useEmailSettings()
const { user } = useLocalStorage()

const senderName = ref('')
const senderEmailLocal = ref('')
const senderSubdomainInput = ref('')
const savedSenderEmail = ref('')
const isLocked = ref(false)
const isConfirmSenderModalOpen = ref(false)

const emailLocalPattern = /^[a-zA-Z.-]$/

const handleSenderEmailKeydown = (event: KeyboardEvent) => {
  if (event.ctrlKey || event.metaKey) return
  if (event.key.length === 1 && !emailLocalPattern.test(event.key)) {
    event.preventDefault()
  }
}

const sanitizeSubdomain = (value: string): string => {
  let subdomain = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9.-]/g, '')
  const verified = (props.domain ?? '').toLowerCase()
  if (verified) {
    if (subdomain === verified) subdomain = ''
    else if (subdomain.endsWith(`.${verified}`)) subdomain = subdomain.slice(0, -(verified.length + 1))
  }
  return subdomain.replace(/^\.+/, '').replace(/\.+$/, '')
}

const senderSubdomain = computed({
  get: () => senderSubdomainInput.value,
  set: (value: string) => {
    senderSubdomainInput.value = sanitizeSubdomain(value)
  }
})

const deriveSubdomain = (host: string): string => {
  const verified = (props.domain ?? '').toLowerCase()
  const normalizedHost = host.toLowerCase()
  if (!verified || !normalizedHost || normalizedHost === verified) return ''
  return normalizedHost.endsWith(`.${verified}`)
    ? normalizedHost.slice(0, normalizedHost.length - verified.length - 1)
    : ''
}

const senderEmail = computed(() => {
  if (!senderEmailLocal.value || !props.domain) return ''
  const host = senderSubdomainInput.value ? `${senderSubdomainInput.value}.${props.domain}` : props.domain
  return `${senderEmailLocal.value}@${host}`
})

watch(
  () => props.domain,
  () => {
    if (!savedSenderEmail.value) return
    senderSubdomainInput.value = deriveSubdomain(savedSenderEmail.value.split('@')[1] ?? '')
  }
)

const handleOpenConfirmSenderModal = () => {
  isConfirmSenderModalOpen.value = true
}

const handleSaveSettings = async () => {
  isConfirmSenderModalOpen.value = false
  const result = await saveEmailSettings({
    sender_name: senderName.value,
    sender_email: senderEmail.value
  })
  if (result) {
    savedSenderEmail.value = senderEmail.value
    isLocked.value = true
  }
}

onMounted(async () => {
  const settings = await getEmailSettings()
  if (settings) {
    senderName.value = settings.sender_name
    savedSenderEmail.value = settings.sender_email ?? ''
    senderEmailLocal.value = savedSenderEmail.value.split('@')[0] || ''
    senderSubdomainInput.value = deriveSubdomain(savedSenderEmail.value.split('@')[1] ?? '')
    isLocked.value = settings.is_locked ?? false
  } else {
    senderName.value = user.value.merchant.name
  }
})
</script>

<template>
  <div class="grid grid-cols-1 lg:grid-cols-12 gap-4">
    <Card class="lg:col-span-8">
      <CardContent class="flex flex-col h-full justify-between gap-6">
        <div class="flex flex-col gap-6">
          <div>
            <h2 class="text-lg font-semibold text-foreground">
              {{ t('pages.domain.sender.title') }}
            </h2>
            <p class="text-sm text-muted-foreground mt-1">
              {{ t('pages.domain.sender.description') }}
            </p>
          </div>

          <div class="grid gap-4">
            <div class="flex flex-col gap-1">
              <label for="senderName" class="text-sm text-foreground">
                {{ t('pages.domain.sender.name') }}
              </label>
              <Input id="senderName" v-model="senderName" :disabled="isLocked || !domain" />
            </div>
            <div class="flex flex-col gap-1">
              <label for="senderEmail" class="text-sm text-foreground">
                {{ t('pages.domain.sender.email') }}
              </label>
              <div
                class="flex rounded-control border border-input shadow-xs focus-within:border-ring focus-within:ring-ring/50 focus-within:ring-[3px] transition-[color,box-shadow]"
              >
                <Input
                  id="senderEmail"
                  v-model="senderEmailLocal"
                  :disabled="isLocked || !domain"
                  class="rounded-r-none border-0 shadow-none focus-visible:border-0 focus-visible:ring-0 flex-1 min-w-0"
                  @keydown="handleSenderEmailKeydown"
                />
                <span
                  class="inline-flex items-center bg-muted px-2 text-sm text-foreground-secondary border-x border-border"
                >
                  @
                </span>
                <Input
                  id="senderSubdomain"
                  v-model="senderSubdomain"
                  :disabled="isLocked || !domain"
                  :placeholder="t('pages.domain.sender.subdomainPlaceholder')"
                  class="rounded-none border-0 shadow-none focus-visible:border-0 focus-visible:ring-0 flex-1 min-w-0"
                />
                <span
                  class="inline-flex items-center rounded-r-control bg-muted px-3 text-sm text-foreground-secondary whitespace-nowrap border-l border-border"
                >
                  .{{ domain }}
                </span>
              </div>
              <p class="text-xs text-muted-foreground">{{ t('pages.domain.sender.subdomainHint') }}</p>
            </div>
          </div>
        </div>

        <BaseTip severity="info" :text="t('pages.domain.sender.lockedTooltip')" />

        <div class="w-auto mr-auto">
          <Button
            variant="default-primary"
            :disabled="!senderName || !senderEmailLocal || isLocked || isLoadingSettings"
            @click="handleOpenConfirmSenderModal"
          >
            <Loader2 v-if="isLoadingSettings" class="h-4 w-4 animate-spin" />
            {{ t('pages.domain.sender.save') }}
          </Button>
        </div>
      </CardContent>
    </Card>

    <Card class="lg:col-span-4 overflow-hidden lg:rounded-b-none">
      <CardContent>
        <PreviewExpeditor :sender-name="senderName" :sender-email="senderEmail" />
      </CardContent>
    </Card>

    <BaseModal
      :is-open="isConfirmSenderModalOpen"
      :text-title="t('pages.domain.sender.confirmModal.title')"
      :cancel="t('pages.domain.config.buttons.cancel')"
      :link-text="t('pages.domain.sender.save')"
      :is-processing="isLoadingSettings"
      icon="IconoirWarningTriangle"
      icon-color="orange"
      size="lg"
      @close="isConfirmSenderModalOpen = false"
      @click="handleSaveSettings"
    >
      <p class="text-center text-sm text-foreground-secondary whitespace-pre-line">
        {{ t('pages.domain.sender.confirmModal.description') }}
      </p>
    </BaseModal>
  </div>
</template>
